import React, { useEffect, useRef, useState } from "react";
import FilterDesk from "../Components/FilterDesk";
import { useDispatch, useSelector } from "react-redux";
import { getPropertyListThunk } from "../../thunks/BookThunks";
import DeskList from "./DeskList";
import SizeBox from "../../../../components/SizeBox";
import {
  filterMarketSelector,
  propertyFilterSelector,
} from "../../selectors/BookSelectors";
import ModalConfirmBookingDesk from "../Components/ModalConfirmBookingDesk";
import ModalBookingSubmittedDesk from "../Components/ModalBookingSubmittedDesk";
import ModalCheckin from "../Components/ModalCheckin";
import { propertyFilterAction } from "../../actions/BookActions";
import { convertDate2StatEndTimeUsingMoment } from "../../services/Common";
import moment from "moment-timezone";

const Desk = () => {
  const dispatch = useDispatch();
  const propertyFilter = useSelector(propertyFilterSelector);
  const { city_code, page } = propertyFilter;
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [populateFromSessionStorage, setPopulateFromSessionStorage] =
    useState(false);
  const filterMarket = useSelector(filterMarketSelector);

  // The property list filter is updated from several places (Desk's own
  // defaults, FilterDesk, the Market switcher) and those updates can land as
  // separate-but-equivalent dispatches. Rather than rely on object identity to
  // dedupe, we compare a normalized signature of the request-relevant fields
  // and skip the fetch when nothing meaningful changed — guaranteeing a single
  // call per actual filter change.
  const lastFetchKeyRef = useRef<string | null>(null);
  const buildFetchKey = (filter: Record<string, any>) =>
    JSON.stringify(
      Object.keys(filter)
        .sort()
        .map((key) => {
          const value = filter[key];
          // Normalize complex values (moment/Date) to a stable primitive and
          // undefined to null so equivalent filters serialize identically.
          if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            typeof value.valueOf === "function"
          ) {
            return [key, value.valueOf()];
          }
          return [key, value ?? null];
        })
    );

  useEffect(() => {
    const defaultDate = sessionStorage.getItem("DEFAULT_DATE");
    const defaultLocation = sessionStorage.getItem("DEFAULT_LOCATION");
    const defaultAmenity = sessionStorage.getItem("DEFAULT_AMENITIES");
    const defaultSort = Number(sessionStorage.getItem("DEFAULT_SORTBY"));

    let startTime = null;
    let endTime = null;

    if (defaultDate && filterMarket?.timezone) {
      const defaultDateMomentObj = moment(defaultDate);
      const time = convertDate2StatEndTimeUsingMoment(
        filterMarket?.timezone,
        defaultDateMomentObj
      );
      startTime = time.startTime;
      endTime = time.endTime;
    }

    const defaultFilters = {
      ...propertyFilter,
      sort_by: defaultSort ? defaultSort : propertyFilter?.sort_by,
      amenities: defaultAmenity
        ? defaultAmenity.split(",").map(Number)
        : propertyFilter?.amenities,
      lat: defaultLocation ? JSON.parse(defaultLocation).lat : null,
      lng: defaultLocation ? JSON.parse(defaultLocation).lng : null,
      start_time: startTime,
      end_time: endTime,
    };

    dispatch(propertyFilterAction(defaultFilters));
    setPopulateFromSessionStorage(true);
  }, [populateFromSessionStorage]);

  useEffect(() => {
    if (!populateFromSessionStorage) return;
    (async () => {
      if (!city_code) {
        return;
      }

      const fetchKey = buildFetchKey(propertyFilter);
      if (fetchKey === lastFetchKeyRef.current) {
        return;
      }
      lastFetchKeyRef.current = fetchKey;

      try {
        if (page === 1) {
          setLoading(true);
        } else {
          setLoadMoreLoading(true);
        }
        await dispatch<any>(getPropertyListThunk(propertyFilter));
      } finally {
        if (page === 1) {
          setLoading(false);
        } else {
          setLoadMoreLoading(false);
        }
      }
    })();
  }, [propertyFilter]);

  return (
    <>
      <FilterDesk />
      <SizeBox height={"16px"} />
      <DeskList loading={loading} loadMoreLoading={loadMoreLoading} />
      <ModalConfirmBookingDesk />
      <ModalBookingSubmittedDesk />
      <ModalCheckin />
    </>
  );
};

export default Desk;
